import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const AgentSelectionModal = ({ isOpen, onClose, onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [maxAgents, setMaxAgents] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      fetchData();
    }
  }, [isOpen, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Fetch subscription and agent selection requirements
      const [subscriptionResponse, agentsResponse] = await Promise.all([
        fetch(`${backendUrl}/api/subscription/agent-selection-required`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/subscription/available-agents`, { credentials: 'include' })
      ]);

      if (subscriptionResponse.ok && agentsResponse.ok) {
        const subscriptionData = await subscriptionResponse.json();
        const agentsData = await agentsResponse.json();
        
        setSubscription(subscriptionData);
        setAvailableAgents(agentsData.agents);
        setMaxAgents(subscriptionData.max_agents || 0);
        setSelectedAgents(subscriptionData.selected_agents || []);
      } else {
        setError('Failed to load agent selection data');
      }
    } catch (error) {
      console.error('Error fetching agent selection data:', error);
      setError('Error loading agent selection data');
    } finally {
      setLoading(false);
    }
  };

  const handleAgentToggle = (agentId) => {
    setSelectedAgents(prev => {
      if (prev.includes(agentId)) {
        return prev.filter(id => id !== agentId);
      } else {
        if (prev.length >= maxAgents) {
          // For Navigator (1 agent), replace the selection
          if (maxAgents === 1) {
            return [agentId];
          }
          // For Advocate (4 agents), don't allow more than 4
          return prev;
        }
        return [...prev, agentId];
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedAgents.length === 0) {
      setError('Please select at least one agent');
      return;
    }

    if (maxAgents === 1 && selectedAgents.length !== 1) {
      setError('Please select exactly one agent for your Navigator plan');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/subscription/select-agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ selected_agents: selectedAgents })
      });

      if (response.ok) {
        const data = await response.json();
        if (onComplete) {
          onComplete(data);
        }
        onClose();
        // Redirect to dashboard
        navigate('/');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save agent selection');
      }
    } catch (error) {
      console.error('Error saving agent selection:', error);
      setError('Error saving agent selection');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanInfo = () => {
    if (maxAgents === 1) {
      return {
        title: 'Navigator Plan - Select Your AI Agent',
        description: 'Choose ONE AI agent that you\'ll have access to. This selection will be locked until your next billing cycle.',
        badge: 'BASIC - $39/month'
      };
    } else if (maxAgents === 4) {
      return {
        title: 'Advocate Plan - Select Your AI Agents',
        description: 'Choose up to FOUR AI agents for your professional plan. This selection will be locked until your next billing cycle.',
        badge: 'PROFESSIONAL - $79/month'
      };
    }
    return {
      title: 'Select Your AI Agents',
      description: 'Choose the AI agents you want to access.',
      badge: 'CUSTOM'
    };
  };

  if (!isOpen) return null;

  const planInfo = getPlanInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-4">
              <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                {planInfo.badge}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{planInfo.title}</h2>
            <p className="text-gray-400">{planInfo.description}</p>
            {maxAgents === 1 && (
              <div className="mt-2 text-yellow-400 text-sm">
                ⚠️ You can select only ONE agent for your Navigator plan
              </div>
            )}
            {maxAgents === 4 && (
              <div className="mt-2 text-blue-400 text-sm">
                📊 You can select up to FOUR agents for your Advocate plan
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto"></div>
              <p className="text-gray-400 mt-2">Loading agents...</p>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="bg-red-900/30 border border-red-400 text-red-400 p-4 rounded-lg mb-6 text-center">
                  {error}
                </div>
              )}

              {/* Agent Selection Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {availableAgents.map((agent) => {
                  const isSelected = selectedAgents.includes(agent.id);
                  const isDisabled = !isSelected && selectedAgents.length >= maxAgents;
                  
                  return (
                    <div
                      key={agent.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-900/20'
                          : isDisabled
                          ? 'border-gray-600 bg-gray-800/50 opacity-50 cursor-not-allowed'
                          : 'border-gray-600 bg-gray-800 hover:border-blue-400'
                      }`}
                      onClick={() => !isDisabled && handleAgentToggle(agent.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-white font-semibold mb-2">{agent.name}</h3>
                          <p className="text-gray-400 text-sm">{agent.description}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ml-4 ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-400'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selection Summary */}
              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <h4 className="text-white font-semibold mb-2">Your Selection:</h4>
                {selectedAgents.length > 0 ? (
                  <div className="space-y-1">
                    {selectedAgents.map(agentId => {
                      const agent = availableAgents.find(a => a.id === agentId);
                      return agent ? (
                        <div key={agentId} className="text-blue-400 text-sm">
                          ✓ {agent.name}
                        </div>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No agents selected</p>
                )}
                <div className="mt-2 text-xs text-gray-500">
                  {selectedAgents.length} of {maxAgents} agents selected
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || selectedAgents.length === 0}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    submitting || selectedAgents.length === 0
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    'Confirm Selection'
                  )}
                </button>
              </div>

              {/* Important Notice */}
              <div className="mt-6 p-4 bg-yellow-900/30 border border-yellow-400 rounded-lg">
                <div className="flex items-start">
                  <span className="text-yellow-400 mr-2">⚠️</span>
                  <div className="text-yellow-300 text-sm">
                    <p className="font-semibold mb-1">Important:</p>
                    <p>Your agent selection will be locked until your next billing cycle. Choose carefully as you won't be able to change your selection during your current subscription period.</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentSelectionModal;